import { FC } from 'react';
import {
  CircularProgress,
  HStack,
  Image,
  useBoolean,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import { chakra } from '@chakra-ui/system';

import chillDudeImg from 'assets/brand/chill.png';
import creditCardImg from 'assets/brand/credit-card.png';
import folderImg from 'assets/brand/folder.png';
import SunshineIcon from 'components/_core/SunshineIcon';
import SunshineSquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import {
  VerificationStatus,
  VerificationStep,
  VerificationStepCategory,
} from 'features/Onboarding/modules/Verification/lib/steps/types';

import locales from './locales';

type CategoryProps = {
  category: VerificationStepCategory;
  verificationSteps: VerificationStep[];
  displayOnlyRemainingSteps: boolean;
  onStepClick: (step: VerificationStep) => void;
};

const CATEGORY_IMAGES: Record<VerificationStepCategory, string> = {
  [VerificationStepCategory.CompanyInfo]: folderImg,
  [VerificationStepCategory.PersonalInfo]: chillDudeImg,
  [VerificationStepCategory.ShineOffer]: creditCardImg,
};

const getSubtitle = (remainingStepsCount: number) => {
  if (remainingStepsCount === 0) {
    return locales.subtitle.complete;
  }
  if (remainingStepsCount === 1) {
    return locales.formatString(locales.subtitle.remainingTasksSingular, {
      count: remainingStepsCount,
    });
  }
  return locales.formatString(locales.subtitle.remainingTasksPlural, {
    count: remainingStepsCount,
  });
};

const Category: FC<CategoryProps> = ({
  category,
  displayOnlyRemainingSteps,
  onStepClick,
  verificationSteps,
}) => {
  const steps = verificationSteps.filter(
    (step) =>
      step.metadata.category === category &&
      (!displayOnlyRemainingSteps ||
        step.status === VerificationStatus.Invalid ||
        step.status === VerificationStatus.ToDo),
  );

  // We don't want the hover effects to trigger on touch devices
  // which is the default browsers' behaviour
  const [isHoverEnabled] = useMediaQuery('(hover: hover)');

  const remainingStepsCount = steps.filter(
    ({ status }) =>
      status !== VerificationStatus.Completed &&
      status !== VerificationStatus.Validated,
  ).length;

  const [isOpen, { toggle }] = useBoolean(remainingStepsCount > 0);

  if (!steps.length) {
    return null;
  }

  return (
    <VStack
      _hover={
        isHoverEnabled
          ? { borderColor: 'grey.400', cursor: 'pointer' }
          : undefined
      }
      align="stretch"
      border="1px solid"
      borderColor="grey.300"
      borderRadius="radius-8"
      gap={0}
      overflow="hidden"
      width="full"
    >
      <HStack
        background="grey.100"
        borderBottom={isOpen ? '1px solid' : undefined}
        borderColor="grey.300"
        justifyContent="space-between"
        onClick={toggle}
        paddingLeft="space-16"
        paddingRight="space-32"
        paddingY="space-20"
      >
        <HStack gap="space-12">
          <Image src={CATEGORY_IMAGES[category]} width={54} />
          <VStack align="stretch" gap="space-4">
            <Text fontWeight="weight-500" size="lg" variant="primary">
              {locales.title[category]}
            </Text>
            <HStack align="center" gap="space-8">
              {remainingStepsCount > 0 ? (
                <CircularProgress
                  color="primary"
                  max={1}
                  size={14}
                  thickness={12}
                  value={1 - remainingStepsCount / steps.length || 0.1}
                />
              ) : (
                <SunshineIcon
                  color="success"
                  name="tick-in-circle"
                  size="icon-16"
                />
              )}
              <Text size="sm" variant="secondary">
                {getSubtitle(remainingStepsCount)}
              </Text>
            </HStack>
          </VStack>
        </HStack>
        <SunshineIcon
          color="light"
          name={isOpen ? 'chevron-up' : 'chevron-down'}
          size="icon-24"
        />
      </HStack>
      {isOpen ? (
        <VStack align="stretch" gap={0} padding="space-16">
          {steps.map((step) => {
            const { errors, metadata, status } = step;
            const { isDisabled } = metadata;

            const label =
              metadata.label[
                status === VerificationStatus.ToDo ? 'add' : 'review'
              ];

            const description =
              metadata.description?.[
                status === VerificationStatus.ToDo ? 'add' : 'review'
              ];

            const error =
              status === VerificationStatus.Invalid
                ? errors.join('\n') ||
                  metadata.description?.review ||
                  locales.stepErrorFallback
                : undefined;

            return (
              <HStack
                _hover={
                  isHoverEnabled
                    ? {
                        background: 'grey.100',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                      }
                    : undefined
                }
                borderRadius="radius-8"
                justifyContent="space-between"
                key={step.id}
                onClick={isDisabled ? undefined : () => onStepClick(step)}
                opacity={isDisabled ? 0.7 : 1}
                padding="space-16"
              >
                <HStack gap="space-16">
                  {status === VerificationStatus.Completed ||
                  status === VerificationStatus.Validated ? (
                    <SunshineSquircleIcon colorScheme="green" name="tick" />
                  ) : (
                    <SunshineSquircleIcon name={metadata.iconName} />
                  )}
                  <VStack align="stretch" gap={0}>
                    <Text fontWeight="weight-500" size="md" variant="primary">
                      {label}
                    </Text>
                    {error ? (
                      <Text color="orange.700" size="sm">
                        {error}
                      </Text>
                    ) : null}
                    {!error && description ? (
                      <chakra.p as={Text} size="sm" variant="secondary">
                        {description}
                      </chakra.p>
                    ) : null}
                  </VStack>
                </HStack>
                {isDisabled ? null : (
                  <SunshineIcon
                    color="light"
                    name="chevron-right"
                    size="icon-24"
                  />
                )}
              </HStack>
            );
          })}
        </VStack>
      ) : null}
    </VStack>
  );
};

export default Category;
